// RAGBOT specific component/file
import getRouterBasename from '@/lib/router';
import { toast } from 'sonner';

import { ClientError } from '@chainlit/react-client';

type Payload = FormData | any;
type RequestParams = Record<string, string | number | boolean | undefined>;

interface APIRequestOptions {
  request?: RequestParams;
  body?: Payload;
  headers?: { Authorization?: string; 'Content-Type'?: string };
}

export class APIBase {
  constructor(
    public httpEndpoint: string,
    public on401?: () => void,
    public onError?: (error: ClientError) => void
  ) {}

  buildEndpoint(path: string, params?: RequestParams) {
    let url = this.httpEndpoint.endsWith('/')
      ? `${this.httpEndpoint.slice(0, -1)}${path}`
      : `${this.httpEndpoint}${path}`;
    if (params && Object.keys(params).length > 0) {
      const query = Object.entries(params)
        .filter(([_, v]) => v !== undefined && v !== null)
        .map(
          ([k, v]) =>
            `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
        )
        .join('&');
      url += url.includes('?') ? '&' + query : '?' + query;
    }
    return url;
  }

  private async getDetailFromErrorResponse(
    res: Response
  ): Promise<string | undefined> {
    try {
      const body = await res.json();
      return body?.detail;
    } catch (error: any) {
      console.error('Unable to parse error response', error);
    }
    return undefined;
  }

  private handleRequestError(error: any) {
    if (error instanceof ClientError) {
      if (error.status === 401 && this.on401) {
        this.on401();
      }
      if (this.onError) {
        this.onError(error);
      }
    }
    console.error(error);
  }

  async fetch(
    method: string,
    path: string,
    options: APIRequestOptions = {}
  ): Promise<Response> {
    try {
      const { request, body: data, headers = {} } = options;

      let body;
      let contentHeaders = { ...headers };

      if (method !== 'GET' && method !== 'HEAD') {
        if (data instanceof FormData) {
          body = data;
        } else {
          contentHeaders['Content-Type'] = 'application/json';
          body = data ? JSON.stringify(data) : null;
        }
      }

      const res = await fetch(this.buildEndpoint(path, request), {
        method,
        credentials: 'include',
        headers: contentHeaders,
        body
      });

      if (!res.ok) {
        const detail = await this.getDetailFromErrorResponse(res);
        throw new ClientError(res.statusText, res.status, detail);
      }

      return res;
    } catch (error: any) {
      this.handleRequestError(error);
      throw error;
    }
  }

  async get(endpoint: string, options: APIRequestOptions = {}) {
    const { request, headers } = options;
    return await this.fetch('GET', endpoint, { request, headers });
  }

  async post(endpoint: string, options: APIRequestOptions = {}) {
    const { request, body, headers } = options;
    return await this.fetch('POST', endpoint, {
      request,
      body,
      headers
    });
  }
}

const devServer = 'http://localhost:8000' + getRouterBasename();
const url = import.meta.env.DEV
  ? devServer
  : window.origin + getRouterBasename();
const serverUrl = new URL(url);
const httpEndpoint = serverUrl.toString();

class RateLimitAPI extends APIBase {
  async verifyCaptcha(identifier: string, token: string) {
    await this.post('/rate-limit/captcha', {
      request: { identifier },
      body: { token }
    });
  }

  async getRateLimitStatus(identifier: string) {
    const res = await this.get('/rate-limit/status', {
      request: { identifier: identifier }
    });
    const body = await res.json();
    return body['status'];
  }
}

const onError = (error: ClientError) => {
  //toast.error(error.toString()); // DISABLE TOAST
};

export const rateLimitApiClient = new RateLimitAPI(
  httpEndpoint,
  undefined,
  onError
);
